// Assistente IA (BETA, pedido 2026-10-20) — chat do cliente no app com
// agenda e gastos PESSOAIS (nada a ver com a Agenda/Financeiro da empresa)
// e a configuração em Administração > Módulos > Assistente IA. Backend:
// gpsdrive-backend src/ai-assistant/. A resposta é gerada em segundo plano:
// o envio devolve a mensagem "pending" e a tela acompanha a conversa.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

export type AiFunctionTool = "none" | "agenda" | "gastos" | "imagem" | "saude";

export interface AiFunction {
  id: string;
  name: string;
  emoji: string;
  description: string;
  prompt: string;
  tool: AiFunctionTool;
  enabled: boolean;
  sortOrder: number;
}

export type AiFunctionInput = Omit<AiFunction, "id">;

export interface AiAdminSettings {
  enabled: boolean;
  audience: "testers" | "all";
  testerEmails: string[];
  name: string;
  avatarUrl: string | null;
  welcomeMessage: string;
  companyPresentation: string;
  basePrompt: string;
  openaiKeyMasked: string | null;
  openaiModel: string;
  geminiKeyMasked: string | null;
  geminiImageModel: string;
  dailyMessageLimit: number;
  themeColor: string | null;
  functions: AiFunction[];
  usage: { messages30d: number; users30d: number; tokens30d: number };
  defaults: { basePrompt: string; welcomeMessage: string };
}

export interface AiAdminSettingsInput {
  enabled: boolean;
  audience: "testers" | "all";
  testerEmails: string[];
  name: string;
  welcomeMessage: string;
  companyPresentation: string;
  basePrompt: string;
  /** undefined = mantém, "" = apaga */
  openaiKey?: string;
  openaiModel: string;
  geminiKey?: string;
  geminiImageModel: string;
  dailyMessageLimit: number;
  themeColor: string | null;
}

export interface AiMe {
  allowed: boolean;
  beta?: boolean;
  configured?: boolean;
  imageEnabled?: boolean;
  enabled?: boolean;
  name?: string;
  avatarUrl?: string | null;
  welcomeMessage?: string;
  themeColor?: string | null;
  functions?: { id: string; name: string; emoji: string; description: string; tool: AiFunctionTool }[];
}

export interface AiAttachment {
  kind: "image" | "audio" | "document";
  name: string;
  mimeType: string;
  url: string;
  transcript?: string;
  generated: boolean;
}

export interface AiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  status: "pending" | "done" | "error";
  createdAt: string;
  attachments: AiAttachment[];
  actions?: { label: string; message: string }[];
}

export interface AiConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface AiConversation {
  id: string;
  title: string;
  messages: AiMessage[];
}

export interface AiOutgoingAttachment {
  name: string;
  mimeType: string;
  dataBase64: string;
}

/** Data/hora local de Brasília: date "YYYY-MM-DD", time "HH:mm". */
export interface AiPersonalEventInput {
  title: string;
  notes: string;
  date: string;
  time: string;
  remindMinutesBefore: number | null;
}

export interface AiPersonalEvent {
  id: string;
  title: string;
  notes: string;
  startsAt: string;
  remindMinutesBefore: number | null;
}

export interface AiFinanceCategory {
  id: string | null;
  name: string;
  emoji: string;
  monthlyLimitCents: number | null;
  custom: boolean;
  spentCents: number;
}

export interface AiFinance {
  month: string;
  incomeCents: number;
  expenseCents: number;
  leftCents: number;
  balance: { cents: number; setAt: string; informedCents: number } | null;
  categories: AiFinanceCategory[];
  incomes: {
    id: string;
    description: string;
    amountCents: number;
    kind: "fixa" | "avulsa";
    dayOfMonth: number | null;
    receivedOn: string | null;
    inThisMonth: boolean;
  }[];
  items: { id: string; description: string; amountCents: number; category: string; spentOn: string }[];
}

// Aba Saúde (2026-10-25)
export interface AiWorkoutExercise {
  name: string;
  sets: number | null;
  reps: string | null;
  minutes: number | null;
}
export interface AiHealth {
  date: string;
  goals: {
    goal: string;
    stepsGoal: number;
    waterGoalMl: number;
    sleepGoalMin: number;
    kcalGoal: number | null;
    proteinGoalG: number | null;
    carbsGoalG: number | null;
    fatGoalG: number | null;
  };
  day: { steps: number | null; waterMl: number; sleepMin: number | null };
  plans: { id: string; name: string; durationMin: number | null; weekdays: number[]; exercises: AiWorkoutExercise[] }[];
  todayPlan: { id: string; name: string; durationMin: number | null; exercises: AiWorkoutExercise[]; done: number[] } | null;
  meals: { id: string; meal: string; description: string; kcal: number | null; proteinG: number | null; carbsG: number | null; fatG: number | null }[];
  totals: { kcal: number; proteinG: number; carbsG: number; fatG: number };
  week: { date: string; steps: number | null; waterMl: number; sleepMin: number | null; exercisesDone: number }[];
}

export interface AiExpenses {
  month: string;
  totalCents: number;
  byCategory: { category: string; cents: number }[];
  items: { id: string; description: string; amountCents: number; category: string; spentOn: string }[];
}

export type AiNotifyType = "confirmations" | "agenda_reminder" | "agenda_daily" | "expenses_weekly" | "expenses_monthly";
export interface AiNotifyChannels {
  push: boolean;
  whatsapp: boolean;
}
export interface AiNotificationSettings {
  whatsappPhone: string;
  dailyTime: string;
  prefs: Record<AiNotifyType, AiNotifyChannels>;
  types: { key: AiNotifyType; title: string; description: string }[];
  whatsappConnected: boolean;
}

const enc = encodeURIComponent;

export const gpsDriveAiAssistantService = {
  getAdmin: () => gpsDriveGet<AiAdminSettings>("/api/v1/admin/ai-assistant"),
  updateAdmin: (input: AiAdminSettingsInput) => gpsDrivePut<AiAdminSettings>("/api/v1/admin/ai-assistant", input),
  uploadAvatar: (mimeType: string, dataBase64: string) =>
    gpsDrivePost<{ avatarUrl: string }>("/api/v1/admin/ai-assistant/avatar", { mimeType, dataBase64 }),
  removeAvatar: () => gpsDriveDelete<{ avatarUrl: null }>("/api/v1/admin/ai-assistant/avatar"),
  testKeys: () =>
    gpsDrivePost<{ openai: { ok: boolean; message: string }; gemini: { ok: boolean; message: string } }>(
      "/api/v1/admin/ai-assistant/test-keys",
    ),
  createFunction: (input: AiFunctionInput) => gpsDrivePost<AiFunction>("/api/v1/admin/ai-assistant/functions", input),
  updateFunction: (id: string, input: AiFunctionInput) =>
    gpsDrivePut<AiFunction>(`/api/v1/admin/ai-assistant/functions/${enc(id)}`, input),
  deleteFunction: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/admin/ai-assistant/functions/${enc(id)}`),

  me: () => gpsDriveGet<AiMe>("/api/v1/ai-assistant/me"),
  conversations: () => gpsDriveGet<AiConversationSummary[]>("/api/v1/ai-assistant/conversations"),
  conversation: (id: string) => gpsDriveGet<AiConversation>(`/api/v1/ai-assistant/conversations/${enc(id)}`),
  deleteConversation: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/conversations/${enc(id)}`),
  send: (input: { conversationId?: string; text: string; attachments: AiOutgoingAttachment[] }) =>
    gpsDrivePost<{ conversationId: string; userMessage: AiMessage; assistantMessage: AiMessage }>(
      "/api/v1/ai-assistant/messages",
      input,
    ),
  notificationSettings: () => gpsDriveGet<AiNotificationSettings>("/api/v1/ai-assistant/notification-settings"),
  updateNotificationSettings: (input: Pick<AiNotificationSettings, "dailyTime" | "prefs"> & { whatsappPhone: string | null }) =>
    gpsDrivePut<AiNotificationSettings>("/api/v1/ai-assistant/notification-settings", input),
  testNotification: (channel: "push" | "whatsapp") =>
    gpsDrivePost<{ sent: true }>("/api/v1/ai-assistant/notification-settings/test", { channel }),
  events: (range?: { from: string; to: string }) =>
    gpsDriveGet<AiPersonalEvent[]>(
      `/api/v1/ai-assistant/events${range ? `?from=${enc(range.from)}&to=${enc(range.to)}` : ""}`,
    ),
  createEvent: (input: AiPersonalEventInput) => gpsDrivePost<AiPersonalEvent>("/api/v1/ai-assistant/events", input),
  updateEvent: (id: string, input: AiPersonalEventInput) =>
    gpsDrivePut<AiPersonalEvent>(`/api/v1/ai-assistant/events/${enc(id)}`, input),
  deleteEvent: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/events/${enc(id)}`),
  expenses: (month?: string) =>
    gpsDriveGet<AiExpenses>(`/api/v1/ai-assistant/expenses${month ? `?month=${enc(month)}` : ""}`),
  finance: (month?: string) =>
    gpsDriveGet<AiFinance>(`/api/v1/ai-assistant/finance${month ? `?month=${enc(month)}` : ""}`),
  deleteIncome: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/incomes/${enc(id)}`),
  deleteCategory: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/categories/${enc(id)}`),
  deleteExpense: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/expenses/${enc(id)}`),
  health: (date?: string) => gpsDriveGet<AiHealth>(`/api/v1/ai-assistant/health${date ? `?date=${enc(date)}` : ""}`),
  addWater: (date: string, ml: number) => gpsDrivePost<AiHealth>("/api/v1/ai-assistant/health/water", { date, ml }),
  setHealthDay: (input: { date: string; steps?: number | null; sleepMin?: number | null }) =>
    gpsDrivePut<AiHealth>("/api/v1/ai-assistant/health/day", input),
  markExercise: (input: { date: string; planId: string; index: number; done: boolean }) =>
    gpsDrivePost<AiHealth>("/api/v1/ai-assistant/health/exercise", input),
  deleteMeal: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/meals/${enc(id)}`),
  deleteWorkout: (id: string) => gpsDriveDelete<{ deleted: true }>(`/api/v1/ai-assistant/workouts/${enc(id)}`),
};

export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
